import api from '@/lib/api/axios'

export interface ServiceNowConfig {
    id?: number
    instanceUrl: string
    username: string
    apiKey: string
    tableName: string
    customFieldVaultSentinel: string
    customFieldMvc: string
    enabled: boolean
    syncInterval: number
    syncIntervalUnit: 'minutes' | 'hours' | 'days'
    lastSyncAt: string | null
}

export interface CMDBSyncItem {
    id: number
    sysId: string
    ciName: string | null
    ciClass: string | null
    ciDescription: string | null
    ciCategory: string | null
    ciOperationalStatus: string | null
    ciLocation: string | null
    vaultSentinelObjectId: string | null
    isMvc: boolean
    syncStatus: 'new' | 'changed' | 'deleted' | 'synced' | 'ignored'
    changeDetails: string | null
    linkedObjectId: number | null
    lastSeenAt: string | null
}

export interface CMDBSyncStats {
    new: number
    changed: number
    deleted: number
    synced: number
    total: number
}

export interface CMDBSyncLog {
    id: number
    syncType: string
    status: string
    totalCIs: number
    newItems: number
    changedItems: number
    deletedItems: number
    errorMessage: string | null
    startedAt: string
    completedAt: string | null
}

export interface CMDBRelationshipItem {
    id: number
    sysId: string
    parentSysId: string
    childSysId: string
    parentCiName: string | null
    childCiName: string | null
    parentObjectId: string | null
    childObjectId: string | null
    relationshipType: string | null
    relationshipTypeName: string | null
    syncStatus: 'new' | 'synced' | 'deleted' | 'ignored'
    lastSeenAt: string | null
    createdAt: string | null
}

export interface CMDBRelationshipStats {
    total: number
    new: number
    synced: number
    deleted: number
    ignored: number
}

export const servicenowCmdbApi = {
    getConfig: () =>
        api.get<{ success: boolean; data: ServiceNowConfig }>('/api/servicenow-cmdb/config'),

    saveConfig: (config: Partial<ServiceNowConfig>) =>
        api.put<{ success: boolean; data: { id: number; saved: boolean } }>('/api/servicenow-cmdb/config', config),

    triggerSync: () =>
        api.post<{ success: boolean; data: { syncLogId: number; status: string } }>('/api/servicenow-cmdb/sync'),

    getItems: (params?: { status?: string; page?: number; limit?: number }) =>
        api.get<{ success: boolean; data: CMDBSyncItem[]; page: number; perPage: number; total: number }>('/api/servicenow-cmdb/items', { params }),

    getStats: () =>
        api.get<{ success: boolean; data: CMDBSyncStats }>('/api/servicenow-cmdb/items/stats'),

    addItem: (id: number, objectId?: string) =>
        api.post<{ success: boolean; data: { objectId: string; id: number } }>(`/api/servicenow-cmdb/items/${id}/add`, { objectId }),

    updateItem: (id: number) =>
        api.post<{ success: boolean; data: { objectId: string } }>(`/api/servicenow-cmdb/items/${id}/update`),

    ignoreItem: (id: number) =>
        api.post<{ success: boolean; data: { ignored: boolean } }>(`/api/servicenow-cmdb/items/${id}/ignore`),

    getSyncLog: () =>
        api.get<{ success: boolean; data: CMDBSyncLog[] }>('/api/servicenow-cmdb/sync-log'),

    getPendingCount: () =>
        api.get<{ success: boolean; data: { pendingCount: number } }>('/api/servicenow-cmdb/pending-count'),

    // Relationships
    syncRelationships: () =>
        api.post('/api/servicenow-cmdb/relationships/sync'),

    getRelationships: (params?: { status?: string; limit?: number; offset?: number }) =>
        api.get<{ success: boolean; data: { items: CMDBRelationshipItem[]; total: number } }>('/api/servicenow-cmdb/relationships', { params }),

    getRelationshipStats: () =>
        api.get<{ success: boolean; data: CMDBRelationshipStats }>('/api/servicenow-cmdb/relationships/stats'),

    addRelationship: (id: number, scenario?: number) =>
        api.post(`/api/servicenow-cmdb/relationships/${id}/add`, { scenario: scenario || 1 }),

    ignoreRelationship: (id: number) =>
        api.post(`/api/servicenow-cmdb/relationships/${id}/ignore`),

    deleteRelationshipMapping: (id: number, scenario?: number) =>
        api.delete(`/api/servicenow-cmdb/relationships/${id}/mapping`, { params: { scenario: scenario || 1 } }),
}
